// Firebase Firestore types for the invoice management system
export interface Client {
  id?: string;
  created_at?: Date;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  license_number?: string | null;
  preferred_contact_method?: string;
}

export interface Bike {
  id?: string;
  created_at?: Date;
  updated_at?: Date;
  model: string;
  make?: string;
  rego_number: string;
  rego_expiry?: string;
  rate_a?: number;
  rate_b?: number;
  daily_rate?: number;
  last_service?: string;
  service_centre?: string;
  service_notes?: string | string[];
  insurance_details?: any | null;
  status?: string;
  notes?: string | null;
}

export interface Case {
  id?: string;
  created_at?: Date;
  updated_at?: Date;
  client_id?: string | null;
  bike_id?: string | null;
  reference_number?: string | null;
  status?: string;
  claim_number?: string | null;
  not_at_fault_name?: string | null;
  not_at_fault_insurance_company?: string | null;
  not_at_fault_policy_number?: string | null;
  at_fault_name?: string | null;
  at_fault_address?: string | null;
  at_fault_phone?: string | null;
  at_fault_email?: string | null;
  at_fault_vehicle?: string | null;
  at_fault_rego?: string | null;
  at_fault_insurance_company?: string | null;
  at_fault_policy_number?: string | null;
  rental_start_date?: string | null;
  rental_end_date?: string | null;
  daily_rate?: number | null;
  total_days?: number;
  current_amount?: number;
  settlement_amount?: number | null;
  notes?: any;
  collecting_for_client?: string | null;
  postal_address?: string | null;
  delivery_address?: string | null;
  bike?: Bike | null;
  client?: Client | null;
}

export interface Document {
  id?: string;
  created_at?: Date;
  case_id: string;
  type: string;
  filename: string;
  ocr_data?: any | null;
  metadata?: any | null;
  file_url?: string;
}

export interface Payment {
  id?: string;
  created_at?: Date;
  case_id: string;
  amount: number;
  payment_date: string;
  payment_method?: string | null;
  reference_number?: string | null;
  notes?: string | null;
}

// Firebase Collection Names
export const COLLECTIONS = {
  CASES: 'cases',
  BIKES: 'bikes',
  DOCUMENTS: 'documents',
  USERS: 'users'
} as const;

// Case Status Enum
export type CaseStatus = 
  | 'new_matter'
  | 'customer_contacted'
  | 'awaiting_approval'
  | 'bike_delivered'
  | 'bike_returned'
  | 'demands_sent'
  | 'awaiting_settlement'
  | 'settlement_agreed'
  | 'paid'
  | 'closed';

// Client Assignment Types
export enum ClientAssignment {
  CRAIG = 'CRAIG',
  ANDREW = 'ANDREW',
  SHARED = 'SHARED'
}

// Bike Status
export type BikeStatus = 'available' | 'delivered' | 'maintenance' | 'decommissioned';

// Service Centre Types
export type ServiceCentre = 'five_dock' | 'seven_hills' | 'reza';

// Document Types
export type DocumentType = 'invoice' | 'receipt' | 'photo' | 'insurance' | 'other';

// Note Types
export type NoteType = 'follow_up' | 'status_change' | 'internal' | 'client_contact';

// Firebase Auth user type
export interface FirebaseUser {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  emailVerified: boolean;
  hasFullAccess?: boolean;
  clientType?: string;
}

// Utility types for Firestore operations
export type WithId<T> = T & { id: string };
export type CreateData<T> = Omit<T, 'id' | 'created_at' | 'updated_at'>;
export type UpdateData<T> = Partial<Omit<T, 'id' | 'created_at'>>;

// Export legacy types for backward compatibility
export type { Client as ClientRow, Bike as BikeRow, Case as CaseRow, Document as DocumentRow, Payment as PaymentRow };

export interface Note {
  timestamp: string;
  content: string;
  type: NoteType;
  from?: string;
  to?: string;
}

export interface User {
  id?: string;
  email: string;
  hasFullAccess?: boolean;
  clientType?: ClientAssignment;
  created_at?: Date | any;
  updated_at?: Date | any;
}

// Form data interfaces
export interface NewCaseData {
  claim_number: string;
  not_at_fault_name: string;
  not_at_fault_insurance_company: string;
  not_at_fault_rego: string;
  at_fault_name: string;
  at_fault_policy_number: string;
  at_fault_insurance_company: string;
  at_fault_rego: string;
  postal_address: string;
  delivery_address: string;
  collecting_for_client: ClientAssignment;
}

export interface UpdateCaseData extends Partial<Case> {
  id: string;
}

export interface CaseFinancials {
  invoiced: number;
  reserve: number;
  settlementAgreed: number;
  paid: number;
}